import { clientResult, type ClientResultSource } from "./clientResult";
import { BaseError } from "make-error";
import type { Result } from "neverthrow";
import { err } from "neverthrow";
import type { LogFunction } from "~/logging/log";
import { logLevels } from "~/logging/levels";

/**
 * Serializable error information.
 *
 * Safe to use in places where it might be sent from server to client, like in `useQuery`.
 */
export interface SerializableErrorInfo {
  /**
   * The (hopefully human-readable) error message,
   * or a string representation of the error if it does not have a message.
   */
  message: string;

  /**
   * The type of the thrown object.
   * In JavaScript, anything can be thrown, not just `Error` objects.
   */
  type: string;

  /**
   * The name of the error, if it exists.
   */
  name?: string;

  /**
   * The cause of the error, if it exists.
   */
  cause?: SerializableErrorInfo;

  /**
   * The full stack trace.
   */
  stack?: string;

  /**
   * The HTTP response code, if we could find one in the error object.
   * Only relevant for errors that are related to HTTP requests.
   */
  statusCode?: number;
}

/**
 * Extract a (hopefully human-readable) message from an error.
 *
 * @param e
 *   An error. This is typed as `unknown` because in JavaScript, we may unfortunately throw anything!
 */
export function getErrorMessage(e: unknown): string {
  if (e instanceof Error || e instanceof BaseError) {
    return e.message;
  }

  if (typeof e === "string") {
    return e;
  }

  if (typeof e === "object" && e !== null && "message" in e) {
    return String(e.message);
  }

  return String(e);
}

/**
 * Extract the name of an error, if it exists.
 *
 * @param e
 *   An error. This is typed as `unknown` because in JavaScript, we may unfortunately throw anything!
 */
export function getErrorName(e: unknown): string | undefined {
  if (e instanceof Error || e instanceof BaseError) {
    return e.name;
  }

  if (typeof e === "object" && e !== null && "name" in e) {
    return e.name ? String(e.name) : undefined;
  }
}

/**
 * Extract the cause of an error, if it exists.
 *
 * @param e
 *   An error. This is typed as `unknown` because in JavaScript, we may unfortunately throw anything!
 */
export function getErrorCause(e: unknown): unknown {
  if (
    e instanceof Error ||
    e instanceof BaseError ||
    (typeof e === "object" && e !== null && "cause" in e)
  ) {
    return e.cause;
  }
}

/**
 * Extract the stack trace of an error, if it exists.
 *
 * @param e
 *   An error. This is typed as `unknown` because in JavaScript, we may unfortunately throw anything!
 */
export function getErrorStack(e: unknown): string | undefined {
  if (e instanceof Error || e instanceof BaseError) {
    return e.stack;
  }

  if (typeof e === "object" && e !== null && "stack" in e) {
    return e.stack ? String(e.stack) : undefined;
  }
}

/**
 * Find an HTTP status code in the given error.
 *
 * @param e
 *   An error. This is typed as `unknown` because in JavaScript, we may unfortunately throw anything!
 */
export function findHttpStatusCode(e: unknown): number | undefined {
  if (typeof e === "object" && e !== null && "response" in e) {
    const response: unknown = e.response;
    if (
      typeof response === "object" &&
      response !== null &&
      "status" in response
    ) {
      const status: unknown = response.status;
      return status ? Number(status) || undefined : undefined;
    }
  }
}

/**
 * Process an `Error` into a `SerializableErrorInfo` object, which is safe to use
 * in places where it might be sent from server to client, like in `useQuery`.
 *
 * @param e
 *   An error. This is typed as `unknown` because in JavaScript, we may unfortunately throw anything!
 */
export function serializeError(e: unknown): SerializableErrorInfo {
  const cause = getErrorCause(e);
  return {
    message: getErrorMessage(e),
    type:
      typeof e === "object"
        ? e === null
          ? "null"
          : e.constructor.name
        : typeof e,
    name: getErrorName(e),
    cause: cause ? serializeError(cause) : undefined,
    stack: getErrorStack(e),
    statusCode: findHttpStatusCode(e),
  };
}

export function redactStackTrace(stack: string): string {
  return (
    stack
      // Remove the file paths, line numbers, and column numbers.
      .replace(/^(\s*at) .*$/gm, "$1 …")

      // Deduplicate lines.
      .split("\n")
      .filter((line, index, lines) => index === 0 || line !== lines[index - 1])
      .join("\n")
  );
}

/**
 * Return a new `SerializableErrorInfo` object that is a copy of the given one,
 * but with all sensitive info from stack traces removed.
 * This is useful for sending errors from the server to the client,
 * where we don't want to expose things like file paths, for security reasons.
 *
 * @param s
 *   A `SerializableErrorInfo` object to process.
 *
 * @return
 *   A new `SerializableErrorInfo` object with redacted stack traces.
 */
export function redactStackTraces(
  s: SerializableErrorInfo,
): SerializableErrorInfo {
  // .replace(/^(\s*at) .*$/gm, "$1 …")
  return {
    message: s.message,
    type: s.type,
    name: s.name,
    cause: s.cause ? redactStackTraces(s.cause) : undefined,
    stack: s.stack ? redactStackTrace(s.stack) : undefined,
    statusCode: s.statusCode,
  };
}

/**
 * Similar to `Result` from `neverthrow`, but serializable, assuming that `T` is serializable.
 */
export type SerializableResult<T> =
  | { value: T; error: undefined }
  | { value: undefined; error: SerializableErrorInfo };

/**
 * Execute the getter while handling server side errors and making them serializable.
 *
 * `T` is assumed to already be serializable.
 *
 * @param getter A function that returns a `ResultAsync` object.
 * @param logServerSideError A callback to log errors when running on the server.
 *
 * @returns A promise that resolves to a serializable version of the `Result` object. The Promise will never reject.
 */
export async function makeResultAsyncSerializable<T>(
  getter: () => ClientResultSource<T, unknown>,
  logServerSideError: LogFunction,
): Promise<SerializableResult<T>> {
  let result: Result<T, unknown>;

  try {
    result = await clientResult(getter());
  } catch (e) {
    // If the getter throws or rejects,
    // we continue as if the error was returned as part of the `Result` object.
    result = err(e);
  }

  // Here we may only return serializable things.
  // `ResultAsync` and `Result` are not serializable, so we must use plain objects.
  // Assume that `T` is serializable.
  if (result.isOk()) {
    return { value: result.value, error: undefined };
  }

  const serializedError = serializeError(result.error);

  if (process.server) {
    logServerSideError({
      level: logLevels.error,
      message: serializedError.message,
      error: result.error,
      extra: { ...serializedError },
    });
  }

  return {
    value: undefined,
    error: process.server
      ? redactStackTraces(serializedError)
      : serializedError,
  };
}
